import { type api, useAPI } from "@/services/api";
import { lightSandbox } from "@/services/lightSandbox";
import { useAnalytics } from "@Light/app/analytics";
import { defaultScaffold } from "@Light/scaffold/default";
import { Provider as ScaffoldProvider } from "@Light/scaffold/Provider";
import { LightSandboxContext } from "@Light/services/lightSandbox";
import type { QueryHookResult } from "@Light/services/queryTypes";
import { createContext, useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { UpdateEmail } from "./account/Details/UpdateEmail";
import { UpdateName } from "./account/Details/UpdateName";
import { HeaderLogo } from "./HeaderLogo";
import { LogoutButton } from "./LogoutButton";

export type AccountState = QueryHookResult<
  typeof api.endpoints.getConfiguration.Types.QueryDefinition
>;

export const ConfigurationContext = createContext<AccountState | undefined>(
  undefined,
);

const standaloneScaffold = {
  ...defaultScaffold,
  page: {
    ...defaultScaffold.page,
    splashImage: false, // PCX-654, temporarily disabled
  },
};

export type WithConfigurationProps = {
  children: React.ReactNode;
};

export const WithConfiguration = ({ children }: WithConfigurationProps) => {
  const { useGetConfigurationQuery } = useAPI();
  const configuration = useGetConfigurationQuery();
  const { setFeatureFlags } = useAnalytics();
  const location = useLocation();
  const onLoginPath = location.pathname.startsWith("/login");

  useEffect(() => {
    if (!configuration.isLoading && configuration.data?.feature_flags) {
      setFeatureFlags(configuration.data.feature_flags);
    }
  }, [
    configuration.data?.feature_flags,
    configuration.isLoading,
    setFeatureFlags,
  ]);

  if (configuration.isLoading && !onLoginPath) {
    return null;
  }

  children = (
    <ConfigurationContext.Provider value={configuration}>
      {children}
    </ConfigurationContext.Provider>
  );

  if (!configuration.data) {
    return (
      <ScaffoldProvider scaffold={standaloneScaffold}>
        <LightSandboxContext.Provider value={lightSandbox}>
          {children}
        </LightSandboxContext.Provider>
      </ScaffoldProvider>
    );
  }

  const showIntervalUsage = (configuration.data.feature_flags ?? []).includes(
    "CUSTOMER_CLIENT_SHOW_INTERVAL_USAGE",
  );
  const headerLogo = configuration.data?.logo ? HeaderLogo : undefined;

  const scaffold = {
    ...standaloneScaffold,
    page: {
      ...standaloneScaffold.page,
      companyName: configuration.data.company_name,
      brandMode: configuration.data.brand_mode,
      legalEntityName: configuration.data.legal_entity_name,
      termsOfServiceUrl: configuration.data.terms_of_service_url ?? undefined,
      supportEmail: configuration.data.support_email,
      supportPhone: configuration.data.support_phone,
      supportHours: configuration.data.support_hours ?? "",
      header: {
        ...defaultScaffold.page.header,
        headerLogo,
        menu: {
          ...defaultScaffold.page.header.menu,
          logoutButton: LogoutButton,
        },
      },
    },
    account: {
      details: {
        updateName: UpdateName,
        updateEmail: UpdateEmail,
      },
    },
    dash: {
      showIntervalUsage,
    },
  };
  return (
    <ScaffoldProvider scaffold={scaffold}>
      <LightSandboxContext.Provider value={lightSandbox}>
        {children}
      </LightSandboxContext.Provider>
    </ScaffoldProvider>
  );
};

export function useConfiguration() {
  const configuration = useContext(ConfigurationContext);
  if (!configuration?.isSuccess || configuration.error) {
    return undefined;
  }

  return configuration.data;
}

export function useRequiredConfiguration() {
  const configuration = useConfiguration();
  if (!configuration) {
    throw new Error(
      "application configuration required to render this component",
    );
  }

  return configuration;
}

export function useFeatureFlag(feature_flag: string) {
  const configuration = useConfiguration();
  if (!configuration) {
    return false;
  }

  return (configuration.feature_flags ?? []).includes(feature_flag);
}
